import jwtDecode from 'jwt-decode';

import api from 'src/api/api';
import store from 'src/store/configureStore';
import { DEVELOPER_MODE } from 'src/constants/config';
import { isCurrentUserDemo } from 'src/utils/authUtils';

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const getCurrentTime = () => {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
};

export class BaseApi {
  /**
   * Check if the current user is in demo mode
   */
  isDemo() {
    return isCurrentUserDemo();
  }

  /**
   * Get the current JWT token from the store
   */
  async getToken() {
    const state = store.getState();
    const { token } = state.jwtToken;
    return token || null;
  }

  /**
   * Create FormData from an object
   */
  createFormData(data) {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        const jsonArray = JSON.stringify(value);
        formData.append(key, jsonArray);
      } else if (value === '') {
        formData.append(key, ' ');
      } else {
        formData.append(key, value);
      }
    });

    return formData;
  }

  /**
   * Create a fake success response for demo mode
   */
  createDemoSuccessResponse(data = {}) {
    return {
      success: true,
      status: 'Query Updated',
      error: false,
      data: {
        success: true,
        status: 'Demo mode: No changes applied',
        message: 'Demo mode: No changes applied',
        ...data,
      },
    };
  }

  /**
   * Make a POST request with automatic retries and token validation
   */
  async postRequest(url, formData, skipToken = false) {
    let retries = 0;
    const maxRetries = 3;
    let token;

    while (retries < maxRetries) {
      try {
        if (!skipToken) {
          token = await this.getToken();

          if (!token) {
            console.warn('Token is not available, retrying...'); // eslint-disable-line no-console
            retries++;
            await sleep(1000);
            continue;
          }

          // Decode token, get exp and compare it to current time, if expired, return null
          const decodedToken = jwtDecode(token);
          const currentTime = Date.now() / 1000;

          if (decodedToken.exp < currentTime) {
            console.warn('Token is expired, retrying...'); // eslint-disable-line no-console
            retries++;
            await sleep(1000);
            continue;
          }
        }

        let response;

        if (formData) {
          if (DEVELOPER_MODE) {
            formData.forEach((value, key) => {
              console.log(key, value); // eslint-disable-line no-console
            });
          }

          response = await api.post(url, formData);
        } else {
          response = await api.post(url, null);
        }

        if (response === null) {
          console.warn('Response is null, retrying...'); // eslint-disable-line no-console
          retries++;
          await sleep(1000);
          continue;
        }

        if (DEVELOPER_MODE) {
          console.group('POST Request Details'); // eslint-disable-line no-console
          console.table({ Timestamp: getCurrentTime(), 'Endpoint URL': url, Token: token }); // eslint-disable-line no-console
          console.table({ 'Response Data': response.data }); // eslint-disable-line no-console
          console.groupEnd(); // eslint-disable-line no-console
        }

        return response.data;
      } catch (error) {
        if (DEVELOPER_MODE) {
          console.warn('Error in postRequest:', error); // eslint-disable-line no-console
          console.warn('Retrying...'); // eslint-disable-line no-console
        }
        retries++;
        await sleep(1000);
      }
    }

    localStorage.clear();
    throw new Error('Max retries exceeded in postRequest');
  }
}
